import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Lock, 
  Shield, 
  Crown, 
  Key, 
  LogOut, 
  Sparkles, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Globe, 
  Search, 
  Check, 
  Laptop, 
  DollarSign, 
  RotateCcw, 
  PlusCircle, 
  Activity, 
  TrendingUp,
  Send,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { 
  loadPaperAccount, 
  resetPaperAccount, 
  depositPaperFunds, 
  getPaperStatistics 
} from '../services/paperTradingService';
import { 
  TIMEZONE_OPTIONS, 
  getSavedTimezone, 
  getTimezoneLabel, 
  getSystemTimezoneInfo,
  formatDisplayTime 
} from '../services/timezoneService';

export default function UserProfileModal({ isOpen, onClose }) {
  const { 
    user, 
    tier, 
    tierInfo, 
    isAuthenticated, 
    isAdmin, 
    isPro, 
    logout, 
    changePassword, 
    updateUserTimezone, 
    updateUserTelegramChatId, 
    openAuth, 
    setAdminPanelOpen, 
    promptTierGate 
  } = useAuth();

  const [activeTab, setActiveTab] = useState('account'); // 'account' | 'telegram' | 'paper_trading' | 'timezone' | 'password'
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // Telegram Integration State
  const [telegramChatId, setTelegramChatId] = useState('');
  const [tgLoading, setTgLoading] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgSuccess, setTgSuccess] = useState('');
  const [tgError, setTgError] = useState('');
  const [botInfo, setBotInfo] = useState({ bot_username: 'minotrading_bot', bot_url: 'https://t.me/minotrading_bot' });

  // Paper Trading settings
  const [paperAcc, setPaperAcc] = useState(() => loadPaperAccount(user?.id));
  const [paperResetAmount, setPaperResetAmount] = useState(10000);
  const [paperDepositAmount, setPaperDepositAmount] = useState(1000);
  const [paperNotice, setPaperNotice] = useState(null);

  // Timezone search & live time
  const [tzSearch, setTzSearch] = useState('');
  const [tzSuccess, setTzSuccess] = useState('');
  const [nowSec, setNowSec] = useState(Math.floor(Date.now() / 1000));
  const sysInfo = getSystemTimezoneInfo();

  useEffect(() => {
    if (isOpen && user?.id) {
      const acc = loadPaperAccount(user.id);
      setPaperAcc(acc);
      setPaperResetAmount(acc?.initialBalance || 10000);
      setTelegramChatId(user?.telegram_chat_id || '');

      api.getTelegramBotInfo().then((res) => {
        if (res && res.bot_username) {
          setBotInfo(res);
        }
      }).catch(() => {});
    }

    const handleUpdate = (e) => {
      if (e.detail?.userId === (user?.id || 'guest') && e.detail?.account) {
        setPaperAcc(e.detail.account);
        setPaperResetAmount(e.detail.account.initialBalance || 10000);
      }
    };
    window.addEventListener("minopro_paper_account_updated", handleUpdate);
    return () => window.removeEventListener("minopro_paper_account_updated", handleUpdate);
  }, [isOpen, user?.id, user?.telegram_chat_id]);

  const handlePaperReset = (clearHistory = false) => {
    const amt = parseFloat(paperResetAmount) || 10000;
    const updated = resetPaperAccount(user?.id, amt, clearHistory);
    setPaperAcc(updated);
    api.saveUserWorkspace({ paperTrading: updated }).catch(() => {});
    setPaperNotice({ type: 'success', text: `Đã đặt lại số vốn mô phỏng về $${amt.toLocaleString()} USD!` });
    setTimeout(() => setPaperNotice(null), 3500);
  };

  const handlePaperDeposit = () => {
    const amt = parseFloat(paperDepositAmount) || 1000;
    const updated = depositPaperFunds(user?.id, amt);
    setPaperAcc(updated);
    api.saveUserWorkspace({ paperTrading: updated }).catch(() => {});
    setPaperNotice({ type: 'success', text: `Đã nạp thêm +$${amt.toLocaleString()} USD vào tài khoản mô phỏng!` });
    setTimeout(() => setPaperNotice(null), 3500);
  };

  const paperStats = useMemo(() => getPaperStatistics(paperAcc?.tradeHistory || []), [paperAcc?.tradeHistory]);

  const handleSaveTelegram = async (e) => {
    if (e) e.preventDefault();
    setTgError('');
    setTgSuccess('');
    setTgLoading(true);
    try {
      if (updateUserTelegramChatId) {
        await updateUserTelegramChatId(telegramChatId);
      } else {
        await api.updateUserTelegram(telegramChatId);
      }
      setTgSuccess('Đã lưu Telegram Chat ID thành công! Các cảnh báo & kèo sẽ tự động gửi về đây.');
      setTimeout(() => setTgSuccess(''), 4500);
    } catch (err) {
      setTgError(err.response?.data?.detail || err.message || 'Lỗi lưu Telegram ID.');
    } finally {
      setTgLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    setTgError('');
    setTgSuccess('');
    if (!telegramChatId || !telegramChatId.trim()) {
      setTgError('Vui lòng nhập Telegram Chat ID trước khi bấm thử gửi.');
      return;
    }
    setTgTesting(true);
    try {
      const res = await api.testUserTelegram(telegramChatId.trim());
      setTgSuccess(res.message || 'Đã gửi tin nhắn test thành công! Hãy mở Telegram để kiểm tra.');
      setTimeout(() => setTgSuccess(''), 6000);
    } catch (err) {
      setTgError(err.response?.data?.detail || err.message || 'Lỗi gửi tin nhắn test. Hãy đảm bảo bạn đã bấm /start với bot trên Telegram!');
    } finally {
      setTgTesting(false);
    }
  };

  if (!isOpen) return null;

  if (!isAuthenticated) {
    onClose();
    openAuth('login');
    return null;
  }

  const currentTimezone = user?.timezone || getSavedTimezone();

  const handleSelectTimezone = (tzId) => {
    if (updateUserTimezone) {
      updateUserTimezone(tzId);
    }
    setTzSuccess(`Đã lưu múi giờ: ${getTimezoneLabel(tzId)} vào tài khoản!`);
    setTimeout(() => setTzSuccess(''), 3000);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdError('');
    setPwdSuccess('');

    if (!oldPassword || !newPassword) {
      setPwdError('Vui lòng nhập đầy đủ mật khẩu cũ và mới.');
      return;
    }
    if (newPassword.length < 6) {
      setPwdError('Mật khẩu mới phải có ít nhất 6 ký tự.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Mật khẩu xác nhận không khớp.');
      return;
    }

    setPwdLoading(true);
    try {
      await changePassword(oldPassword, newPassword);
      setPwdSuccess('Đổi mật khẩu thành công!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwdError(err.response?.data?.detail || err.message || 'Lỗi đổi mật khẩu.');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-3 sm:p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col font-sans max-h-[90vh]">
        
        {/* Top Gradient Banner */}
        <div className="h-1.5 w-full bg-gradient-to-r from-cyan-500 via-amber-400 to-purple-500 shrink-0" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 border-b border-slate-800 bg-slate-950/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm border shadow-md ${
              tier === 'ADMIN'
                ? 'bg-purple-950/60 border-purple-500/50 text-purple-300'
                : tier === 'PRO'
                ? 'bg-amber-950/60 border-amber-500/50 text-amber-300'
                : 'bg-cyan-950/60 border-cyan-500/50 text-cyan-300'
            }`}>
              {user.username ? user.username.slice(0, 2).toUpperCase() : 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-slate-100">{user.username}</h3>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold border ${tierInfo.color}`}>
                  {tierInfo.badge}
                </span>
              </div>
              <p className="text-[11px] text-slate-400">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav Tabs */}
        <div className="flex border-b border-slate-800 px-4 sm:px-6 pt-2 bg-slate-950/30 gap-3 sm:gap-4 overflow-x-auto shrink-0">
          <button
            onClick={() => setActiveTab('account')}
            className={`pb-2 text-xs font-bold transition-all border-b-2 whitespace-nowrap cursor-pointer ${
              activeTab === 'account'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Thông Tin
          </button>
          <button
            onClick={() => setActiveTab('telegram')}
            className={`pb-2 text-xs font-bold transition-all border-b-2 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'telegram'
                ? 'border-sky-500 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Send className="w-3.5 h-3.5 text-sky-400" />
            <span>Kèo Telegram</span>
            {user?.telegram_chat_id && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('paper_trading')}
            className={`pb-2 text-xs font-bold transition-all border-b-2 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'paper_trading'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5" />
            <span>Vốn Thử Nghiệm</span>
          </button>
          <button
            onClick={() => setActiveTab('timezone')}
            className={`pb-2 text-xs font-bold transition-all border-b-2 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'timezone'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Múi Giờ</span>
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`pb-2 text-xs font-bold transition-all border-b-2 whitespace-nowrap cursor-pointer ${
              activeTab === 'password'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            Đổi Mật Khẩu
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">Tên đăng nhập</span>
                  <span className="text-xs text-slate-200 font-bold font-mono">{user.username}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">Email</span>
                  <span className="text-xs text-slate-200 font-bold">{user.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">Gói tài khoản</span>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold border ${tierInfo.color}`}>
                    {tierInfo.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">Telegram nhận kèo</span>
                  {user.telegram_chat_id ? (
                    <span className="text-xs text-emerald-400 font-mono font-bold flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />
                      ID: {user.telegram_chat_id}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveTab('telegram')}
                      className="text-xs text-sky-400 hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <Send className="w-3 h-3" />
                      <span>Chưa liên kết (Bấm để cài)</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">Múi giờ</span>
                  <span className="text-xs text-slate-200 font-bold">{getTimezoneLabel(currentTimezone)}</span>
                </div>
              </div>

              {!isPro && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-slate-950 border border-amber-500/30 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                      <Crown className="w-4 h-4" />
                      <span>Nâng cấp Gói PRO 👑</span>
                    </div>
                    <p className="text-[11px] text-slate-300 mt-1">
                      Nhận cảnh báo &amp; kèo không giới hạn, bot Telegram riêng, AI sâu và Backtest chuyên sâu.
                    </p>
                  </div>
                  <button
                    onClick={() => promptTierGate("Nâng Cấp Gói PRO", "PRO")}
                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold text-xs hover:brightness-110 transition shadow-md shrink-0 cursor-pointer"
                  >
                    Nâng Cấp
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Telegram Settings Tab */}
          {activeTab === 'telegram' && (
            <div className="space-y-4 animate-fadeIn">
              {tgSuccess && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{tgSuccess}</span>
                </div>
              )}

              {tgError && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{tgError}</span>
                </div>
              )}

              {/* Bot Info & Link Header */}
              <div className="p-3.5 rounded-xl bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-slate-950 border border-sky-500/30 space-y-2.5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-sky-500/20 text-sky-400 shrink-0">
                      <Send className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-100 flex items-center gap-1.5">
                        <span>Bot Bắn Tín Hiệu:</span>
                        <strong className="text-sky-300 font-mono">@{botInfo.bot_username || "MinoTradingBot"}</strong>
                      </h4>
                      <p className="text-[10.5px] text-slate-400">
                        Bắn tín hiệu vào lệnh, TP/SL và cảnh báo giá cá nhân hóa
                      </p>
                    </div>
                  </div>

                  <a
                    href={botInfo.bot_url || `https://t.me/${botInfo.bot_username || 'MinoTradingBot'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs transition flex items-center gap-1.5 shadow-sm shadow-sky-500/20 cursor-pointer"
                  >
                    <span>Mở Bot Telegram</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* 2-Step Setup Instructions */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5 text-xs text-slate-300">
                <div className="font-bold text-slate-200 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-amber-400" />
                  <span>Hướng dẫn kết nối chỉ 2 bước:</span>
                </div>
                <div className="space-y-1.5 text-[11px] text-slate-300 leading-relaxed pl-1">
                  <p>
                    <strong>1.</strong> Mở Telegram, tìm <strong>@{botInfo.bot_username || "MinoTradingBot"}</strong> và bấm <strong>START</strong> (hoặc gõ <code>/start</code>).
                  </p>
                  <p>
                    <strong>2.</strong> Nhập số <strong>Telegram Chat ID</strong> của bạn vào ô dưới rồi bấm <em>Lưu Cài Đặt</em>.
                  </p>
                  <p className="text-slate-400 italic">
                    💡 Mẹo: Bạn có thể lấy nhanh mã Chat ID bằng cách nhắn tin cho bot <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">@userinfobot</a> hoặc <a href="https://t.me/raw_data_bot" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">@raw_data_bot</a> trên Telegram.
                  </p>
                </div>
              </div>

              {/* Telegram Form */}
              <form onSubmit={handleSaveTelegram} className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                    Mã Telegram Chat ID Của Bạn
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                      <Send className="w-4 h-4 text-sky-400" />
                    </div>
                    <input
                      type="text"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                      placeholder="Ví dụ: 987654321 hoặc -100123456789"
                      required
                      className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs font-mono font-bold text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500 transition"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleTestTelegram}
                    disabled={tgTesting || !telegramChatId}
                    className="flex-1 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-bold text-xs transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5 text-sky-400" />
                    <span>{tgTesting ? 'Đang thử gửi...' : '⚡ Thử Gửi Tin Nhắn'}</span>
                  </button>

                  <button
                    type="submit"
                    disabled={tgLoading}
                    className="flex-1 py-2 rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-400 hover:to-blue-400 text-slate-950 font-bold text-xs transition cursor-pointer shadow-md shadow-sky-500/20 text-center"
                  >
                    {tgLoading ? 'Đang lưu...' : '💾 Lưu Cài Đặt Telegram'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Paper Trading Tab */}
          {activeTab === 'paper_trading' && (
            <div className="space-y-4 animate-fadeIn">
              {paperNotice && (
                <div className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                  paperNotice.type === 'success'
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                    : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                }`}>
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  <span>{paperNotice.text}</span>
                </div>
              )}

              {/* Statistics Card */}
              <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <span className="font-bold text-xs text-slate-200 flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Thống Kê Tài Khoản Đang Đánh</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 font-bold">
                    ID: {paperAcc?.accountId || `SIM-${(user?.id || user?.username || 'DEMO').toString().slice(-6).toUpperCase()}`}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                    <span className="text-[10px] text-slate-400 block mb-0.5">Số Dư (Balance)</span>
                    <strong className="font-mono text-slate-100 text-sm">
                      ${Number(paperAcc?.balance ?? 10000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                    <span className="text-[10px] text-slate-400 block mb-0.5">Tổng Lãi/Lỗ Đã Đóng</span>
                    <strong className={`font-mono text-sm ${
                      (paperStats?.netPnL ?? paperStats?.totalPnL ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                    }`}>
                      {(paperStats?.netPnL ?? paperStats?.totalPnL ?? 0) >= 0 ? '+' : ''}${Number(paperStats?.netPnL ?? paperStats?.totalPnL ?? 0).toFixed(2)}
                    </strong>
                  </div>
                  <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                    <span className="text-[10px] text-slate-400 block mb-0.5">Tỷ Lệ Thắng (Win Rate)</span>
                    <strong className="font-mono text-cyan-300 text-sm">
                      {Number(paperStats?.winRate ?? 0).toFixed(1)}% ({paperStats?.wins ?? paperStats?.winTrades ?? 0}/{paperStats?.totalTrades ?? paperStats?.totalClosed ?? 0})
                    </strong>
                  </div>
                  <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800/80">
                    <span className="text-[10px] text-slate-400 block mb-0.5">Profit Factor</span>
                    <strong className="font-mono text-amber-300 text-sm">
                      {Number(paperStats?.profitFactor ?? 1).toFixed(2)}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Reset Capital Card */}
              <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-slate-100 flex items-center gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5 text-amber-400" />
                    <span>Thiết Lập Lại Số Vốn Ban Đầu</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Nhập số vốn mới để đặt lại tài khoản mô phỏng theo mong muốn của bạn.
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="100"
                    step="500"
                    value={paperResetAmount}
                    onChange={(e) => setPaperResetAmount(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-100 text-right focus:outline-none focus:border-amber-500"
                    placeholder="Điền số vốn ban đầu..."
                  />
                  <span className="font-bold text-slate-400 font-mono text-xs">USD</span>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handlePaperReset(false)}
                    className="py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-bold text-xs transition cursor-pointer text-center"
                  >
                    Chỉ Đặt Lại Vốn
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePaperReset(true)}
                    className="py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs transition cursor-pointer shadow-md shadow-amber-500/20 text-center"
                  >
                    Xóa Hết &amp; Làm Mới
                  </button>
                </div>
              </div>

              {/* Deposit Virtual Funds Card */}
              <div className="p-4 rounded-xl bg-slate-950/70 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-slate-100 flex items-center gap-1.5">
                    <PlusCircle className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Nạp Thêm Tiền Ảo Vào Số Dư</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Cộng thêm tiền ảo vào số dư hiện tại mà không làm thay đổi các lệnh đang mở hay lịch sử giao dịch.
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="100"
                    step="500"
                    value={paperDepositAmount}
                    onChange={(e) => setPaperDepositAmount(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-mono font-bold text-emerald-300 text-right focus:outline-none focus:border-emerald-500"
                    placeholder="Số tiền nạp thêm..."
                  />
                  <span className="font-bold text-slate-400 font-mono text-xs">USD</span>
                </div>

                <button
                  type="button"
                  onClick={handlePaperDeposit}
                  className="w-full py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs transition cursor-pointer shadow-md shadow-emerald-500/20 text-center"
                >
                  Nạp Thêm Vốn Vào Tài Khoản
                </button>
              </div>
            </div>
          )}

          {/* Timezone Settings Tab */}
          {activeTab === 'timezone' && (
            <div className="space-y-3 animate-fadeIn">
              {tzSuccess && (
                <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center gap-2 animate-fadeIn">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  <span>{tzSuccess}</span>
                </div>
              )}

              {/* Current Computer Time Banner */}
              <div className="p-2.5 bg-cyan-950/40 rounded-xl border border-cyan-800/40 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-cyan-300">
                  <Laptop className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>
                    Giờ máy tính: <strong>{new Date().toLocaleTimeString('vi-VN')}</strong> ({sysInfo.offsetStr})
                  </span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-mono font-bold">
                  LOCAL
                </span>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Tìm kiếm quốc gia, thành phố, múi giờ..."
                  value={tzSearch}
                  onChange={(e) => setTzSearch(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition"
                />
              </div>

              {/* Options List */}
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                {TIMEZONE_OPTIONS.filter((opt) => {
                  const q = tzSearch.toLowerCase();
                  return (
                    opt.label.toLowerCase().includes(q) ||
                    opt.desc.toLowerCase().includes(q) ||
                    (opt.iana && opt.iana.toLowerCase().includes(q))
                  );
                }).map((opt) => {
                  const isSelected = currentTimezone === opt.id;
                  const liveTime = formatDisplayTime(nowSec, opt.id, "full");

                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleSelectTimezone(opt.id)}
                      className={`w-full text-left p-2.5 rounded-xl border transition flex items-center justify-between cursor-pointer ${
                        isSelected
                          ? "bg-amber-500/10 border-amber-500/50 shadow-sm"
                          : "bg-slate-950/50 border-slate-800/80 hover:bg-slate-950 hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-base">{opt.flag}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold ${isSelected ? "text-amber-300" : "text-slate-200"}`}>
                              {opt.label}
                            </span>
                            {isSelected && (
                              <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-500 text-slate-950">
                                ĐANG DÙNG
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-400 block">{opt.desc}</span>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-[11px] font-mono font-bold text-slate-300 block">{liveTime}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-amber-400 inline-block ml-auto mt-0.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Change Password Form */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-3.5 animate-fadeIn">
              {pwdError && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwdError}</span>
                </div>
              )}

              {pwdSuccess && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwdSuccess}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Mật Khẩu Hiện Tại
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <Key className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Nhập mật khẩu đang dùng"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Mật Khẩu Mới
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Tối thiểu 6 ký tự"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
                  Xác Nhận Mật Khẩu Mới
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu mới"
                    className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={pwdLoading}
                className="w-full py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2"
              >
                {pwdLoading ? 'Đang cập nhật...' : 'Lưu Mật Khẩu Mới'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3.5 border-t border-slate-800 flex items-center justify-between bg-slate-950/60 text-xs shrink-0">
          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="px-3 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 font-bold transition flex items-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Đăng Xuất</span>
          </button>

          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold transition cursor-pointer"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}
